/**
 * ArtistAvatar — image-or-initials avatar with deterministic gradient
 * background derived from the artist's name. Used everywhere we render
 * a small artist tile (similar artists, related artists, portrait card)
 * so missing images never look broken — they look intentional.
 *
 * Props:
 *   - name      : string (required, used for initials + color hash)
 *   - src       : string|null (image URL; if falsy, initials are shown)
 *   - size      : number (px diameter; default 80)
 *   - shape     : 'circle' | 'square' (default 'circle')
 *   - className : string (extra wrapper classes)
 *
 * Color hashing: simple deterministic djb2-style hash → palette index.
 * Palette stays inside GroovLens's brand range (teal/lime/amber/coral
 * plus a few harmonious extensions) so avatars never clash with the UI.
 */
import { useMemo, useState } from 'react';

// Curated palette — analogous to GroovLens's brand. Each entry is a
// {from, to} gradient pair so avatars get depth, not flat color.
const PALETTE = [
  { from: 'hsl(187 87% 38%)', to: 'hsl(187 87% 22%)' },  // teal
  { from: 'hsl(78 95% 58%)',  to: 'hsl(78 70% 38%)'  },  // lime
  { from: 'hsl(32 100% 64%)', to: 'hsl(32 80% 44%)'  },  // amber
  { from: 'hsl(0 100% 71%)',  to: 'hsl(0 70% 48%)'   },  // coral
  { from: 'hsl(280 70% 60%)', to: 'hsl(280 55% 38%)' },  // purple
  { from: 'hsl(330 80% 65%)', to: 'hsl(330 55% 42%)' },  // pink
  { from: 'hsl(210 90% 60%)', to: 'hsl(210 70% 36%)' },  // blue
  { from: 'hsl(160 70% 50%)', to: 'hsl(160 60% 28%)' },  // mint
];

function hashIndex(str, mod) {
  let h = 5381;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0; // djb2, unsigned
  }
  return h % mod;
}

function initialsOf(name) {
  const parts = String(name || '?')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    // Single word: first 1-2 letters (e.g., "Drake" -> "D", "Wh0" -> "W")
    const w = parts[0];
    return w.length >= 2 ? (w[0] + w[1]).toUpperCase() : w[0].toUpperCase();
  }
  // Multi-word: first letter of first two significant words
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function ArtistAvatar({
  name,
  src,
  size = 80,
  shape = 'circle',
  className = '',
}) {
  // Track image error to gracefully fall back to initials if src 404s
  const [imageOk, setImageOk] = useState(true);

  const { initials, gradient } = useMemo(() => {
    const idx = hashIndex(name, PALETTE.length);
    const pair = PALETTE[idx];
    return {
      initials: initialsOf(name),
      gradient: `linear-gradient(135deg, ${pair.from} 0%, ${pair.to} 100%)`,
    };
  }, [name]);

  const radius = shape === 'circle' ? '9999px' : '12%';
  // Initials sized as a fraction of avatar diameter so any size scales cleanly.
  const fontSize = Math.max(11, Math.round(size * 0.36));

  const showImage = !!src && imageOk;

  return (
    <div
      className={`overflow-hidden grid place-items-center select-none ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: showImage ? undefined : gradient,
      }}
      data-testid="artist-avatar"
      data-has-image={showImage ? 'true' : 'false'}
    >
      {showImage ? (
        <img
          src={src}
          alt={name || 'Artist'}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setImageOk(false)}
        />
      ) : (
        <span
          className="font-display font-bold text-white tracking-tight"
          style={{
            fontSize,
            textShadow: '0 1px 2px rgba(0,0,0,0.35)',
          }}
          aria-label={`${name || 'Unknown'} avatar`}
        >
          {initials}
        </span>
      )}
    </div>
  );
}
